import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { setLogout } from "../store/slices/userSlice";
import { useAdmin } from "../hooks/useAdmin";
import UserInfoPanel from "../components/settings/UserInfoPanel";
import AdminSection from "../components/settings/AdminSection";
import ProUpgradeSection from "../components/settings/ProUpgradeSection";
import DartsSection from "../components/settings/DartsSection";

/**
 * Main Settings Page Component
 *
 * This component serves as the main container for all settings-related functionality.
 * It conditionally renders different sections based on user authentication and permissions.
 *
 * Features:
 * - User authentication check
 * - Admin-only sections (debug mode, notifications, minimum rounds)
 * - Pro upgrade section for non-pro users
 * - Dart management section
 * - User logout functionality
 */
const SettingsPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isLoggedIn, userInfo, proMember } = useSelector(
    (state) => state.user
  );
  const { isAdminUser } = useAdmin();

  const handleLogout = () => {
    dispatch(setLogout());
  };

  // Early return for unauthenticated users
  if (!isLoggedIn) {
    return (
      <div className="p-6 text-center text-test">
        <h1 className="text-2xl font-semibold mb-4">{t("settings.title")}</h1>
        <p className="text-white/80">{t("settings.pleaseLogin")}</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* User Information Panel */}
        <UserInfoPanel userInfo={userInfo} />

        {/* Darts Management Section */}
        <DartsSection />
        {/* Admin-only Sections */}
        {isAdminUser && <AdminSection />}

        {/* Pro Upgrade Section - Only for non-pro users */}
        {!proMember && <ProUpgradeSection userInfo={userInfo} />}
        {/* Logout Section */}
        <div className="text-center">
          <button
            onClick={handleLogout}
            className="px-6 py-3 bg-test text-primary rounded-xl shadow-md hover:opacity-90 transition-opacity duration-200"
            aria-label={t("settings.logout")}
          >
            {t("settings.logout")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
